import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import { Checkbox } from 'components/ui/Checkbox';

const PrivacyPolicy = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [activeSection, setActiveSection] = useState(null);
  const [privacySettings, setPrivacySettings] = useState({
    analytics: true,
    marketing: false,
    thirdParty: false,
    personalization: true
  });

  const lastUpdated = 'January 26, 2026';
  const effectiveDate = 'January 26, 2026';
  const version = '1.0';

  const sections = [
    {
      id: 'introduction',
      title: '1. Introduction',
      icon: 'Shield',
      content: [
        'TrainaryGym ("we", "our", or "us") is committed to protecting your privacy and ensuring the security of your personal information.',
        'This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform and services.',
        'By using TrainaryGym, you consent to the data practices described in this policy. If you do not agree with this policy, please do not use our services.'
      ]
    },
    {
      id: 'collection',
      title: '2. Information We Collect',
      icon: 'Database',
      content: [
        '2.1 Personal Information: We collect information you provide directly, including name, email address, phone number, date of birth, payment information, and emergency contact details.',
        '2.2 Fitness Data: We collect workout history, attendance records, class bookings, personal training sessions, body measurements, fitness goals, and progress tracking data.',
        '2.3 Usage Data: We automatically collect information about your interactions with the Platform, including IP address, device type, browser type, pages visited, and time spent on pages.',
        '2.4 Location Data: With your permission, we collect location data to provide location-based services and facility check-in functionality.',
        '2.5 Communication Data: We store messages, support tickets, and other communications you have with us or other users through the Platform.',
        '2.6 Payment Information: Payment card details are securely processed by our payment processor (Stripe) and are not stored on our servers.'
      ]
    },
    {
      id: 'usage',
      title: '3. How We Use Your Information',
      icon: 'Settings',
      content: [
        '3.1 Service Delivery: To provide, maintain, and improve our services, process transactions, and manage your membership.',
        '3.2 Personalization: To customize your experience, provide personalized workout recommendations, and track your fitness progress.',
        '3.3 Communication: To send you service updates, promotional materials (with your consent), and respond to your inquiries.',
        '3.4 Analytics: To understand how users interact with our Platform and improve functionality and user experience.',
        '3.5 Security: To detect, prevent, and address technical issues, fraud, and security vulnerabilities.',
        '3.6 Legal Compliance: To comply with legal obligations, resolve disputes, and enforce our agreements.',
        '3.7 AI Features: To power AI-driven features including workout recommendations, retention insights, and help assistance.'
      ]
    },
    {
      id: 'sharing',
      title: '4. Information Sharing and Disclosure',
      icon: 'Share2',
      content: [
        '4.1 Gym Owners and Staff: Your information is shared with the gym(s) where you hold membership for service delivery and facility management.',
        '4.2 Trainers: If you work with a personal trainer, relevant fitness data and progress information is shared with them.',
        '4.3 Service Providers: We share data with third-party service providers who perform services on our behalf (payment processing, SMS notifications, analytics).',
        '4.4 Business Transfers: In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.',
        '4.5 Legal Requirements: We may disclose information when required by law, court order, or to protect our rights and safety.',
        '4.6 Aggregated Data: We may share anonymized, aggregated data that does not identify you personally for research and analytics purposes.',
        '4.7 With Your Consent: We may share information for other purposes with your explicit consent.'
      ]
    },
    {
      id: 'thirdparty',
      title: '5. Third-Party Services',
      icon: 'Link',
      content: [
        '5.1 Payment Processing: We use Stripe for secure payment processing. Stripe\'s privacy policy governs their handling of your payment information.',
        '5.2 SMS Notifications: We use Twilio for SMS communications. Message and data rates may apply.',
        '5.3 AI Services: We use OpenAI for AI-powered features. Data sent to OpenAI is processed according to their privacy policy.',
        '5.4 Analytics: We may use analytics services to understand Platform usage and improve our services.',
        '5.5 Third-Party Links: Our Platform may contain links to third-party websites. We are not responsible for their privacy practices.'
      ]
    },
    {
      id: 'security',
      title: '6. Data Security',
      icon: 'Lock',
      content: [
        '6.1 Security Measures: We implement industry-standard security measures including encryption, secure servers, and access controls to protect your data.',
        '6.2 Data Encryption: Sensitive data is encrypted in transit using SSL/TLS and at rest using AES-256 encryption.',
        '6.3 Access Controls: Access to personal data is restricted to authorized personnel who need it to perform their job functions.',
        '6.4 Regular Audits: We conduct regular security audits and vulnerability assessments to maintain data protection.',
        '6.5 Incident Response: We have procedures in place to respond to data breaches and will notify affected users as required by law.',
        '6.6 No Guarantee: While we strive to protect your data, no method of transmission over the internet is 100% secure.'
      ]
    },
    {
      id: 'retention',
      title: '7. Data Retention',
      icon: 'Archive',
      content: [
        '7.1 Active Accounts: We retain your personal data for as long as your account is active or as needed to provide services.',
        '7.2 Inactive Accounts: Data from inactive accounts may be retained for up to 3 years for legal and business purposes.',
        '7.3 Legal Requirements: Some data may be retained longer to comply with legal obligations, resolve disputes, and enforce agreements.',
        '7.4 Deletion Requests: Upon request, we will delete your personal data unless retention is required by law.',
        '7.5 Backup Systems: Deleted data may persist in backup systems for up to 90 days before permanent deletion.'
      ]
    },
    {
      id: 'rights',
      title: '8. Your Privacy Rights',
      icon: 'UserCheck',
      content: [
        '8.1 Access: You have the right to access the personal data we hold about you.',
        '8.2 Correction: You can request correction of inaccurate or incomplete personal data.',
        '8.3 Deletion: You can request deletion of your personal data ("right to be forgotten").',
        '8.4 Data Portability: You can request a copy of your data in a structured, machine-readable format.',
        '8.5 Opt-Out: You can opt out of marketing communications at any time.',
        '8.6 Restrict Processing: You can request restriction of processing of your personal data in certain circumstances.',
        '8.7 Object: You can object to processing of your personal data for direct marketing purposes.',
        '8.8 Withdraw Consent: Where processing is based on consent, you can withdraw consent at any time.'
      ]
    },
    {
      id: 'cookies',
      title: '9. Cookies and Tracking',
      icon: 'Cookie',
      content: [
        '9.1 What Are Cookies: Cookies are small text files stored on your device that help us provide and improve our services.',
        '9.2 Types of Cookies: We use essential cookies (required for Platform functionality), analytics cookies (to understand usage), and preference cookies (to remember your settings).',
        '9.3 Third-Party Cookies: Some third-party services we use may set their own cookies.',
        '9.4 Cookie Management: You can control cookies through your browser settings, but disabling cookies may affect Platform functionality.',
        '9.5 Do Not Track: We currently do not respond to Do Not Track signals.'
      ]
    },
    {
      id: 'children',
      title: '10. Children\'s Privacy',
      icon: 'Users',
      content: [
        '10.1 Age Requirement: TrainaryGym is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.',
        '10.2 Parental Consent: For users between 13 and 18, parental or guardian consent is required for membership.',
        '10.3 Discovery of Child Data: If we discover we have collected data from a child under 13 without parental consent, we will delete it promptly.',
        '10.4 Parent Rights: Parents can request access to, correction of, or deletion of their child\'s information.'
      ]
    },
    {
      id: 'international',
      title: '11. International Data Transfers',
      icon: 'Globe',
      content: [
        '11.1 Data Location: Your information may be transferred to and processed in countries other than your country of residence.',
        '11.2 Safeguards: We implement appropriate safeguards to ensure your data receives adequate protection in accordance with applicable laws.',
        '11.3 EU-US Transfers: For transfers from the EU, we comply with applicable data protection frameworks and regulations.',
        '11.4 Your Consent: By using TrainaryGym, you consent to the transfer of your information to other countries.'
      ]
    },
    {
      id: 'gdpr',
      title: '12. GDPR Compliance (EU Users)',
      icon: 'FileCheck',
      content: [
        '12.1 Legal Basis: We process your data based on contract performance, legal obligations, legitimate interests, and your consent.',
        '12.2 Data Controller: TrainaryGym acts as the data controller for personal data collected through the Platform.',
        '12.3 Data Protection Officer: You can contact our Data Protection Officer at dpo@trainarygym.com.',
        '12.4 Supervisory Authority: You have the right to lodge a complaint with your local data protection authority.',
        '12.5 EU Rights: EU users have additional rights under GDPR including data portability and the right to object to automated decision-making.'
      ]
    },
    {
      id: 'ccpa',
      title: '13. CCPA Rights (California Users)',
      icon: 'Scale',
      content: [
        '13.1 Right to Know: California residents can request information about personal data we collect, use, and disclose.',
        '13.2 Right to Delete: You can request deletion of personal data we have collected.',
        '13.3 Right to Opt-Out: You can opt out of the sale of personal data (note: we do not sell personal data).',
        '13.4 Non-Discrimination: We will not discriminate against you for exercising your CCPA rights.',
        '13.5 Authorized Agent: You can designate an authorized agent to make requests on your behalf.'
      ]
    },
    {
      id: 'changes',
      title: '14. Changes to This Policy',
      icon: 'RefreshCw',
      content: [
        '14.1 Policy Updates: We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements.',
        '14.2 Notification: We will notify you of material changes by email or through a prominent notice on the Platform.',
        '14.3 Effective Date: Changes become effective on the date specified in the updated policy.',
        '14.4 Continued Use: Your continued use of the Platform after changes indicates acceptance of the updated policy.',
        '14.5 Version History: Previous versions of this policy are available upon request.'
      ]
    },
    {
      id: 'contact',
      title: '15. Contact Us',
      icon: 'Mail',
      content: [
        'For questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact us:',
        'Privacy Team Email: privacy@trainarygym.com',
        'Data Protection Officer: dpo@trainarygym.com',
        'Mailing Address: TrainaryGym Privacy Department',
        'Support Portal: Available through the AI Help Assistant in the Platform',
        'Response Time: We aim to respond to all privacy inquiries within 30 days.'
      ]
    }
  ];

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev?.[sectionId]
    }));
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setExpandedSections(prev => ({ ...prev, [sectionId]: true }));
    }
  };

  const filteredSections = sections?.filter(section =>
    searchQuery === '' ||
    section?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    section?.content?.some(text => text?.toLowerCase()?.includes(searchQuery?.toLowerCase()))
  );

  const handlePrivacySettingChange = (setting) => {
    setPrivacySettings(prev => ({
      ...prev,
      [setting]: !prev?.[setting]
    }));
  };

  const handleSavePreferences = () => {
    // In a real implementation, this would save to backend
    alert('Privacy preferences saved successfully!');
  };

  const handleDownloadData = () => {
    alert('Data download request submitted. You will receive an email with your data within 30 days.');
  };

  const handleDeleteAccount = () => {
    const confirmed = window.confirm(
      'Are you sure you want to request account deletion? This action cannot be undone.'
    );
    if (confirmed) {
      alert('Account deletion request submitted. Our team will process your request within 30 days.');
    }
  };

  return (
    <div className="min-h-screen bg-background py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors mb-4"
          >
            <Icon name="ArrowLeft" size={20} />
            <span className="text-sm font-medium">Back to Login</span>
          </Link>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-h2 font-heading text-foreground mb-2">Privacy Policy</h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Icon name="Calendar" size={16} />
                  <span>Effective: {effectiveDate}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="RefreshCw" size={16} />
                  <span>Last Updated: {lastUpdated}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="FileText" size={16} />
                  <span>Version {version}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                icon={<Icon name="Printer" />}
                onClick={() => window.print()}
              >
                Print
              </Button>
              <Button
                variant="outline"
                size="sm"
                icon={<Icon name="Download" />}
                onClick={() => alert('PDF download functionality would be implemented here')}
              >
                PDF
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Icon
              name="Search"
              size={20}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              type="text"
              placeholder="Search privacy policy..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Privacy Controls */}
        <div className="card p-6 mb-6">
          <h3 className="text-h5 font-heading text-foreground mb-4 flex items-center gap-2">
            <Icon name="Sliders" size={20} />
            Privacy Preferences
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Manage your privacy settings and control how your data is used.
          </p>

          <div className="space-y-3 mb-4">
            <label className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors">
              <div className="flex-1">
                <div className="font-medium text-foreground">Analytics Cookies</div>
                <div className="text-sm text-muted-foreground">Help us improve by collecting usage data</div>
              </div>
              <Checkbox
                checked={privacySettings?.analytics}
                onChange={() => handlePrivacySettingChange('analytics')}
              />
            </label>

            <label className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors">
              <div className="flex-1">
                <div className="font-medium text-foreground">Marketing Communications</div>
                <div className="text-sm text-muted-foreground">Receive promotional emails and offers</div>
              </div>
              <Checkbox
                checked={privacySettings?.marketing}
                onChange={() => handlePrivacySettingChange('marketing')}
              />
            </label>

            <label className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors">
              <div className="flex-1">
                <div className="font-medium text-foreground">Third-Party Data Sharing</div>
                <div className="text-sm text-muted-foreground">Share data with partner services</div>
              </div>
              <Checkbox
                checked={privacySettings?.thirdParty}
                onChange={() => handlePrivacySettingChange('thirdParty')}
              />
            </label>

            <label className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors">
              <div className="flex-1">
                <div className="font-medium text-foreground">Personalization</div>
                <div className="text-sm text-muted-foreground">Customize experience based on your activity</div>
              </div>
              <Checkbox
                checked={privacySettings?.personalization}
                onChange={() => handlePrivacySettingChange('personalization')}
              />
            </label>
          </div>

          <Button variant="primary" size="sm" onClick={handleSavePreferences}>
            Save Preferences
          </Button>
        </div>

        {/* Your Data Rights */}
        <div className="card p-6 mb-6">
          <h3 className="text-h5 font-heading text-foreground mb-4 flex items-center gap-2">
            <Icon name="UserCheck" size={20} />
            Your Data Rights
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Exercise your rights regarding your personal data.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              variant="outline"
              size="sm"
              icon={<Icon name="Download" />}
              onClick={handleDownloadData}
              className="justify-start"
            >
              Download My Data
            </Button>
            <Button
              variant="outline"
              size="sm"
              icon={<Icon name="Edit" />}
              onClick={() => alert('Navigate to profile settings to update your information')}
              className="justify-start"
            >
              Update My Information
            </Button>
            <Button
              variant="outline"
              size="sm"
              icon={<Icon name="Mail" />}
              onClick={() => window.location.href = 'mailto:privacy@trainarygym.com'}
              className="justify-start"
            >
              Contact Privacy Team
            </Button>
            <Button
              variant="outline"
              size="sm"
              icon={<Icon name="Trash2" />}
              onClick={handleDeleteAccount}
              className="justify-start text-destructive hover:text-destructive"
            >
              Delete My Account
            </Button>
          </div>
        </div>

        {/* Introduction */}
        <div className="card p-6 mb-6">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Icon name="Shield" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h3 className="text-h5 font-heading text-foreground mb-2">Your Privacy Matters</h3>
              <p className="text-body text-muted-foreground">
                At TrainaryGym, we are committed to protecting your privacy and being transparent about 
                how we collect, use, and share your information. This policy explains our data practices 
                in clear, accessible language.
              </p>
            </div>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="card p-6 mb-6">
          <h3 className="text-h5 font-heading text-foreground mb-4 flex items-center gap-2">
            <Icon name="List" size={20} />
            Table of Contents
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {sections?.map((section) => (
              <button
                key={section?.id}
                onClick={() => scrollToSection(section?.id)}
                className={`flex items-center gap-2 text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  activeSection === section?.id
                    ? 'bg-primary/10 text-primary font-medium' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <Icon name={section?.icon} size={16} />
                {section?.title}
              </button>
            ))}
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {filteredSections?.map((section) => (
            <div
              key={section?.id}
              id={section?.id}
              className="card overflow-hidden scroll-mt-4"
            >
              <button
                onClick={() => toggleSection(section?.id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Icon name={section?.icon} size={20} color="var(--color-primary)" />
                  </div>
                  <h3 className="text-h5 font-heading text-foreground text-left">
                    {section?.title}
                  </h3>
                </div>
                <Icon
                  name={expandedSections?.[section?.id] ? 'ChevronUp' : 'ChevronDown'}
                  size={20}
                  className="text-muted-foreground flex-shrink-0"
                />
              </button>

              {expandedSections?.[section?.id] && (
                <div className="px-6 pb-6 space-y-3 animate-slideDown">
                  {section?.content?.map((paragraph, index) => (
                    <p key={index} className="text-body text-muted-foreground">
                      {paragraph}
                    </p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-8 card p-6">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-success/10">
              <Icon name="CheckCircle" size={24} color="var(--color-success)" />
            </div>
            <div>
              <h4 className="text-h5 font-heading text-foreground mb-2">Questions About Your Privacy?</h4>
              <p className="text-body text-muted-foreground mb-4">
                We're here to help. Contact our privacy team or review our Terms and Conditions 
                for more information about how we protect your data.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/terms-and-conditions">
                  <Button variant="outline" size="sm">
                    View Terms & Conditions
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="sm"
                  icon={<Icon name="Mail" />}
                  onClick={() => window.location.href = 'mailto:privacy@trainarygym.com'}
                >
                  Contact Privacy Team
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;